import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils";

const spinnerVariants = cva("relative inline-block aspect-square transform-gpu", {
  variants: {
    variant: {
      primary: "[&>div]:bg-[#77ab8a]",
      secondary: "[&>div]:bg-[#ab9555]",
      destructive: "[&>div]:bg-destructive",
      muted: "[&>div]:bg-muted-foreground"
    },
    size: {
      sm: "size-7",
      default: "size-8",
      lg: "size-9",
      xl: "size-10",
      "2xl": "size-12"
    }
  },
  defaultVariants: { variant: "primary", size: "default" }
});

export interface SpinnerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    Omit<VariantProps<typeof spinnerVariants>, "size"> {
  className?: string;
  size?: VariantProps<typeof spinnerVariants>["size"] | number;
}

const Spinner = ({ className, variant, size = "default", ...props }: SpinnerProps) => (
  <div
    role="status"
    aria-label="Loading"
    className={cn(
      typeof size === "string" ? spinnerVariants({ variant, size }) : spinnerVariants({ variant }),
      className
    )}
    style={typeof size === "number" ? { width: size, height: size } : undefined}
    {...props}
  >
    {Array.from({ length: 12 }).map((_, i) => (
      <div
        key={i}
        className="animate-sus-spinner absolute left-[46.5%] top-[4.4%] h-[24%] w-[7%] origin-[center_190%] rounded-full opacity-[0.1] will-change-transform"
        style={{
          transform: `rotate(${i * 30}deg)`,
          animationDelay: `${(i * 0.083).toFixed(3)}s`
        }}
        aria-hidden="true"
      />
    ))}
    <span className="sr-only">Loading...</span>
  </div>
);

export { Spinner, spinnerVariants };
